import React from "react";
import PropTypes from "prop-types";
import Alert from "../common/alert/Alert";
import i18n from "i18next";

/**
 * Confirmation modal shown before pasting a scene.
 * Props:
 *   show          {boolean}
 *   counts        {{ images, videos, sounds, narrations }}
 *   crossTraining {boolean}
 *   busy          {boolean}
 *   onCancel      {function}
 *   onConfirm     {function}
 */
export default function ScenePasteModal({
  show,
  counts,
  crossTraining,
  busy,
  onCancel,
  onConfirm,
}) {
  if (!show) return null;

  const { images = 0, videos = 0, sounds = 0, narrations = 0 } = counts || {};

  // Build "Contains: N images, N videos, N sounds, N narrations." — omit zero categories
  const parts = [];
  if (images > 0)
    parts.push(
      images +
        " " +
        (images === 1 ? i18n.t("scenePaste.image", "image") : i18n.t("scenePaste.images", "images"))
    );
  if (videos > 0)
    parts.push(
      videos +
        " " +
        (videos === 1 ? i18n.t("scenePaste.video", "video") : i18n.t("scenePaste.videos", "videos"))
    );
  if (sounds > 0)
    parts.push(
      sounds +
        " " +
        (sounds === 1 ? i18n.t("scenePaste.sound", "sound") : i18n.t("scenePaste.sounds", "sounds"))
    );
  if (narrations > 0)
    parts.push(
      narrations +
        " " +
        (narrations === 1
          ? i18n.t("scenePaste.narration", "narration")
          : i18n.t("scenePaste.narrations", "narrations"))
    );

  const inventoryLine =
    parts.length > 0
      ? i18n.t("scenePaste.contains", "Contains") + ": " + parts.join(", ") + "."
      : null;

  const mediaLine = crossTraining
    ? i18n.t("scenePaste.mediaCopied", "Its media will be copied into this training.")
    : i18n.t("scenePaste.mediaReused", "Its media will be reused.");

  const confirmLabel = busy
    ? i18n.t("scenePaste.copying", "Copying…")
    : i18n.t("scenePaste.addScene", "Add scene");

  return (
    <Alert
      className="pageModal"
      show={show}
      hasHeader
      backdrop="static"
      title={i18n.t("scenePaste.title", "Add scene to this training?")}
      closeButton={false}
      cancelButton
      cancelButtonText={i18n.t("messages.Cancel", "Cancel")}
      acceptButtonText={confirmLabel}
      acceptButtonDisabled={busy}
      onClose={(accepted) => {
        if (accepted) {
          onConfirm();
        } else {
          onCancel();
        }
      }}
    >
      <div>
        {inventoryLine && <p style={{ marginBottom: 4 }}>{inventoryLine}</p>}
        <p style={{ marginBottom: 0, color: "#64748b", fontSize: "0.92em" }}>{mediaLine}</p>
      </div>
    </Alert>
  );
}

ScenePasteModal.propTypes = {
  show: PropTypes.bool,
  counts: PropTypes.shape({
    images: PropTypes.number,
    videos: PropTypes.number,
    sounds: PropTypes.number,
    narrations: PropTypes.number,
  }),
  crossTraining: PropTypes.bool,
  busy: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};
